import { CombineOperator, GetPointResponse, SubTagSearchType, TagSearchType, TopLevelTagSearchType, TopicCategory, UserTag } from '../types';
import { GoogleTagEvent, logWithGoogleTag, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import { categoryFilterFn, likesFilterFn, pointSearch, tagsFilterFn } from '../helpers/filters';
import { customElement, property, query, state } from 'lit/decorators';
import { detectMobileViewport, getCookie } from '../helpers/device';
import { getCategoryIcon, getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { getVisibleTextColor, isGeneratedColorIcon, makeColorsIntoNumberSvg } from '../helpers/colors';
import { isDefined, undefinedIfEmpty } from '../helpers/utils';

import { SettingDrawer } from './mobile/setting-drawer';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { electionVotingIsHappening } from '../helpers/api/election-api';
import { filterPointByRange } from '../helpers/date';
import { getCheckinKey } from '../helpers/api/checkin-api';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';

@customElement('filter-chips')
export class FilterChips extends LitElement {
    static amplitude_callsite: string = 'Filter Chips';

    @query('#wrapperFilters')
    private readonly wrapperFilters!: HTMLDivElement;

    @query('#wrapperTags')
    private readonly wrapperTags!: HTMLDivElement;

    @query('#expanderCategories')
    private readonly expanderCategories!: HTMLDivElement;

    @query('#expanderTags')
    private readonly expanderTags!: HTMLDivElement;

    @query('#leftScrollerCategories')
    private readonly leftScrollerCategories!: HTMLDivElement;

    @query('#leftScrollerTags')
    private readonly leftScrollerTags!: HTMLDivElement;

    @query('#dateTimeRangeModal')
    dateTimeRangeModal!: SettingDrawer;

    @property({ type: Boolean })
    isExpandedCategories: boolean = false;

    @property({ type: Boolean })
    isExpandedTags: boolean = false;

    @property({ type: Boolean })
    isSpecialOfferSelected: boolean = false;

    @property({ type: Boolean })
    show: boolean = false;

    @state()
    selectedCategories: string[] = [];

    @state()
    selectedTopTags: TopLevelTagSearchType[] = [];

    @state()
    selectedSubTags: SubTagSearchType[] = [];

    @state()
    openSubtag?: UserTag;

    @state()
    scrollTimer?: number;

    @state()
    isSelectedCheckedInFilter: boolean = false;

    @state()
    isSelectedPlaceFilter: 1 | 2 | 3 | undefined | 'all' = undefined;

    @state()
    isSelectedApprovalFilter: boolean = false;

    @state()
    isSelectedLikesFilter: boolean = false;

    @state()
    showCheckInFilter: boolean = false;

    @state()
    showElectionPlaceFilter: boolean = false;

    @state()
    hasActivatedChallenge = false;

    @state()
    advancedFilterOn = false;

    @state()
    simpleFilterOn = false;

    @state()
    searchOpen: boolean = false;

    @state()
    pointListOpen: boolean = false;

    @state()
    selectedTags: TagSearchType = {
        operator: CombineOperator.And,
        tags: [],
        };

    @state()
    areTagsOpen: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    private hasCustomCategoryIcon(category: TopicCategory) {
        const icon = category.icon ?? '';

        if (icon.startsWith('#')) {
            return false;
        }

        if (isGeneratedColorIcon(icon)) {
            return false;
        }

        return icon.length > 0;
    }

    private getCategoryChipIcon(category: TopicCategory) {
        if (this.hasCustomCategoryIcon(category)) {
            return getCategoryIcon(category._id);
        }

        if (topicData.topic?.enable_point_numbering === true && topicData.topic?.replace_icons_with_numbers === true) {
            return makeColorsIntoNumberSvg(
                getNumberedPointIconColor(topicData.topic, category._id?.$oid),
                '',
                getNumberedPointIconShape(topicData.topic, category._id?.$oid)
            );
        }

        return getCategoryIcon(category._id);
    }

    firstUpdated() {
        // Add the event listeners
        this.wrapperFilters?.addEventListener('mousewheel', this.handleWheel, false);
        this.wrapperFilters?.addEventListener('DOMMouseScroll', this.handleWheel, false);
        this.wrapperTags?.addEventListener('mousewheel', this.handleWheelTags, false);
        this.wrapperTags?.addEventListener('DOMMouseScroll', this.handleWheelTags, false);

        if (topicData.topic?.show_advanced_filter_row === true) {
            this.areTagsOpen = true;
        }

        if (detectMobileViewport() && topicData.topic?.use_simple_filters_mobile === true) {
            this.simpleFilterOn = true;
        } else if (!detectMobileViewport() && topicData.topic?.use_simple_filters_desktop === true) {
            this.simpleFilterOn = true;
        }

        // Add a listener for the 'languageChanged' event
        translationData.onLanguageChanged(() => {
            this.requestUpdate();
        });

    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
        this.customizeStyles();
        this.updateCheckinFilter();
        this.updateElectionPlaceFilter();
    }

    private updateCheckinFilter() {
        if ((challengeData.allCheckins.size > 0 || challengeData.savedLocationIds.length > 0) && pointCache.points.length > 0) {
            if (isDefined(challengeData.hasActivatedChallenge)) {
                this.hasActivatedChallenge = isDefined(challengeData.hasActivatedChallenge);
            } else {
                this.hasActivatedChallenge = false;
            }

            this.showCheckInFilter = pointCache.points.some((p) =>
                challengeData.allCheckins.get(getCheckinKey(p.location))
            ) || (challengeData.activated_challenge?.personal_locations?.saved !== undefined && challengeData.activated_challenge?.personal_locations.saved.length > 0);
        } else {
            this.showCheckInFilter = false;
        }
    }

    private updateElectionPlaceFilter() {
        this.showElectionPlaceFilter = (topicData.electionHappeningNow?.show_leaders ?? 0) > 0;
    }

    connectedCallback() {
        super.connectedCallback();
        const rbFilters = new ResizeObserver(() => this.resizeSlider(50, false));
        const rbTags = new ResizeObserver(() => this.resizeSlider(0, true));
        setTimeout(() => rbFilters.observe(this.wrapperFilters), 100);
        setTimeout(() => rbTags.observe(this.wrapperTags), 100);
    }

    disconnectedCallback() {
        this.wrapperFilters?.removeEventListener('mousewheel', this.handleWheel, false);
        this.wrapperFilters?.removeEventListener('DOMMouseScroll', this.handleWheel, false);
        this.wrapperTags?.removeEventListener('mousewheel', this.handleWheelTags, false);
        this.wrapperTags?.removeEventListener('DOMMouseScroll', this.handleWheelTags, false);
    }

    private readonly handleWheel = (e: any) => {
        const direction = e.detail < 0 || e.wheelDelta > 0 ? 1 : -1;
        const spaceUnit = -40;
        this.wrapperFilters.scrollLeft += spaceUnit * direction;
        this.handleLeftScrollerVisibility(false);
    };

    private readonly handleWheelTags = (e: any) => {
        const direction = e.detail < 0 || e.wheelDelta > 0 ? 1 : -1;
        const spaceUnit = -40;
        this.wrapperTags.scrollLeft += spaceUnit * direction;
        this.handleLeftScrollerVisibility(true);
    };

    private readonly resizeSlider = (offset: number = 0, selectingTags = false) => {
        const chipClassName = selectingTags ? 'user-tag' : 'custom-chip';
        const target = selectingTags ? this.wrapperTags : this.wrapperFilters;
        const expander = selectingTags ? this.expanderTags : this.expanderCategories;

        const chipButtons: any = this?.shadowRoot?.querySelectorAll(`paper-chip.${chipClassName}`);
        let totalWidth = 0;
        const spacing = 6;

        for (const elem of chipButtons) {
            const chipButton = elem?.shadowRoot?.querySelector('.chip');
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            totalWidth += chipButton.clientWidth + spacing + offset;
        }
        if (isDefined(expander)) {
            if (totalWidth - 10 < target.clientWidth) {
                expander.style.display = 'none';
                target.scrollLeft = 0;
            } else {
                expander.style.display = 'flex';
            }
        }
        this.handleLeftScrollerVisibility(selectingTags);
    };

    private keywordSearch(e:CustomEvent) {
        const searchText = e.detail;
        if (searchText.length > 0) {
            this.searchOpen = false;
            if (searchText !== '') {
                pointCache.filterMarkers({ name: 'ListSearch', filterFn: (p: GetPointResponse) => pointSearch(p, searchText) });
                logWithGoogleTagAndTopicOrganization('autocomplete_search', GoogleTagEvent.MAP_SEARCH, {
                    search_query: searchText,
                });
            }
        }
    }

    // if making changes here, you might also need to be making changes in advanced-filtering-modal
    private handleChipClick({target: { value },}: any, selectingTag = false, categoryOrTagChipName?: string, isSelectedCheckedInFilter = false, isSubTag = false, parentName?: string, isSelectedApprovalFilter = false, isSelectedPlaceFilter: 1 | 2 | 3 | undefined | 'all' = undefined, isSelectedLikesFilter = false) {
        const removeFilters = () => {
            // Don't update the points, since they will get updated when the filters are reapplied.
            pointCache.removeFilter('FilterChip', false /* shouldUpdatePoints */);
        };

        const logClick = () => {
            logWithGoogleTag(FilterChips.amplitude_callsite, GoogleTagEvent.CHIP_CLICKED, {
            categoryOrTagChipName,
            isTag: selectingTag,
            topic_id: topicData.topic?._id?.$oid,
            });
        };

        // tags and subtags are separate because we do ANDs between tags and ORs within Subtags by default
        // example: I want Ice Cream AND (Chocolate OR Vanilla) AND (Cone OR Cup). Ice cream is a top tag, and the rest are subtags that belong to a group
        let tagString: string[] = [];
        let subtagString: string[] = [];
        let categoryString: string[] = [];
        const toggleTopTag = () => {
            if (this.selectedTopTags.some((t) => t.topLevelTag === categoryOrTagChipName)) {
                this.selectedTopTags = this.selectedTopTags.filter((elem) => elem.topLevelTag !== categoryOrTagChipName);
            } else if (categoryOrTagChipName !== undefined) {
                this.selectedTopTags = [...this.selectedTopTags, { topLevelTag: categoryOrTagChipName}];

                logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_TAG_CLICKED, {
                    tag_name: categoryOrTagChipName,
                });
            }
            tagString = this.selectedTopTags.map((t) => t.topLevelTag);
        };

        const toggleSubTag = () => {
            if (parentName === undefined || categoryOrTagChipName === undefined) return;
            const parentTag = this.selectedSubTags.find((t) => t.topLevelTag === parentName);
            if (parentTag) {
                if (parentTag.subTags?.includes(categoryOrTagChipName)) {
                    parentTag.subTags = parentTag.subTags.filter((elem) => elem !== categoryOrTagChipName);
                } else if (categoryOrTagChipName !== undefined) {
                    parentTag.subTags = [...parentTag.subTags, categoryOrTagChipName];
                    logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_TAG_CLICKED, {
                        tag_name: categoryOrTagChipName,
                    });
                }
                // if the parent tag has no sub tags, remove it from selectedSubTags
                if (parentTag.subTags.length === 0) {
                    this.selectedSubTags = this.selectedSubTags.filter((elem) => elem.topLevelTag !== parentName);
                }
            } else {
              this.selectedSubTags = [...this.selectedSubTags, { topLevelTag: parentName, operator: CombineOperator.Or, subTags: [categoryOrTagChipName] }];
                logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_TAG_CLICKED, {
                    tag_name: categoryOrTagChipName,
                });
            }
            subtagString = this.selectedSubTags.map((t) => t.subTags).flat();
        };

        const toggleCategory = () => {
            if (categoryOrTagChipName === undefined) return;
            if (this.selectedCategories.includes(categoryOrTagChipName)) {
                this.selectedCategories = this.selectedCategories.filter((elem) => elem !== categoryOrTagChipName);
            } else if (categoryOrTagChipName !== undefined) {
                this.selectedCategories = [...this.selectedCategories, categoryOrTagChipName];
                logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_CATEGORY_CLICKED, {
                    category_name: categoryOrTagChipName,
                });
            }
            categoryString = this.selectedCategories.map((t) => t);
        };

        const applyFilters = () => {
            const checkedInFilter = this.showCheckInFilter && isSelectedCheckedInFilter
            ? (p: GetPointResponse) => isDefined(challengeData.allCheckins.get(getCheckinKey(p.location))) || challengeData.savedLocationIds.includes(p._id.$oid)
            : (p: GetPointResponse) => true;

            const electionPlaceFilter = this.showElectionPlaceFilter && isSelectedPlaceFilter !== undefined ?
                (p: GetPointResponse) => {
                    const marker = pointCache.getMarker(p._id.$oid);
                    const badges = marker?.badges;
                    if (!badges) return false;
                    if (isSelectedPlaceFilter === 'all') {
                        return badges?.some(badge => badge.filterText?.startsWith('Election Place:'));
                    } else {
                        return badges?.some(badge => badge.filterText === `Election Place: ${isSelectedPlaceFilter}`) ?? false;
                    }
                }
                : (p: GetPointResponse) => true;

            const approvalFilter = pointCache.pointsAwaitingApproval.length > 0 && isSelectedApprovalFilter
            ? (p: GetPointResponse) => pointCache.pointsAwaitingApproval.map((p) => p._id.$oid).includes(p._id.$oid)
            : (p: GetPointResponse) => true;

            const categoryFilter = this.selectedCategories.length >= 1 ? categoryFilterFn : (p: GetPointResponse) => true;
            const tagFilter = this.selectedTags.tags.length >= 1 ? tagsFilterFn : (p: GetPointResponse) => true;
            const likesFilter = topicData.topic?.has_likes === true && this.isSelectedLikesFilter  ? likesFilterFn : (p: GetPointResponse) => true;
            const userCookie = getCookie('user_id') ?? '';

            if (
            this.selectedCategories.length > 0 ||
            this.selectedTopTags.length > 0 ||
            this.selectedSubTags.length > 0 ||
            this.isSelectedCheckedInFilter ||
            this.isSelectedPlaceFilter !== undefined ||
            this.isSelectedApprovalFilter ||
            this.isSelectedLikesFilter
            ) {
                pointCache.filterMarkers({
                    name: 'FilterChip',
                    filterFn: (p: GetPointResponse) =>
                    categoryFilter(p, this.selectedCategories) && tagFilter(p, this.selectedTags) && checkedInFilter(p) && approvalFilter(p) && electionPlaceFilter(p) && likesFilter(p, userCookie),
                });
                pointCache.categoryFilterList = categoryString;
                pointCache.tagFilterList = tagString;
                pointCache.tagFilterList = pointCache.tagFilterList.concat(subtagString);
            } else {
                pointCache.removeFilter('FilterChip', true);
                pointCache.categoryFilterList = [];
                pointCache.tagFilterList = [];
            }
        };

        removeFilters();
        logClick();

        if (isDefined(categoryOrTagChipName)) {
            if (selectingTag && !isSubTag) {
                toggleTopTag();
            } else if (selectingTag && isSubTag && parentName) {
                toggleSubTag();
            } else {
                toggleCategory();
            }
        }

        if (this.selectedSubTags.length === 0) {
            this.selectedTags.tags = this.selectedTopTags;
        } else {
            this.selectedTags.tags = [...this.selectedTopTags, ...this.selectedSubTags];
        }

        this.isSelectedCheckedInFilter = this.showCheckInFilter && isSelectedCheckedInFilter;
        this.isSelectedPlaceFilter = this.showElectionPlaceFilter ? isSelectedPlaceFilter : undefined;
        this.isSelectedApprovalFilter = pointCache.pointsAwaitingApproval.length > 0 && isSelectedApprovalFilter;
        this.isSelectedLikesFilter = topicData.topic?.has_likes === true && isSelectedLikesFilter;
        applyFilters();

        // keep map component in sync
        this.dispatchEvent(new CustomEvent('filter-chips-changed',
            {
                detail: {
                    selectedCategories: this.selectedCategories,
                    selectedTopTags: this.selectedTopTags,
                    selectedSubTags: this.selectedSubTags,
                    isSelectedCheckedInFilter: this.isSelectedCheckedInFilter,
                    isSelectedPlaceFilter: this.isSelectedPlaceFilter,
                    isSpecialOfferSelected: this.isSpecialOfferSelected,
                    isSelectedApprovalFilter: this.isSelectedApprovalFilter,
                    isSelectedLikesFilter: this.isSelectedLikesFilter,
                }
            }
        ));
    }

    private handleHoverLeftScroll(selectingTag = false) {
        const isExpanded = selectingTag ? this.isExpandedTags : this.isExpandedCategories;
        const target = selectingTag ? this.wrapperTags : this.wrapperFilters;

        if (!isExpanded) {
            this.scrollTimer = window.setInterval(() => {
                target.scroll({
                    left: target.scrollLeft - 40,
                    behavior: 'smooth',
                });
                this.handleLeftScrollerVisibility(selectingTag);
            }, 100);
        }
    }

    private handleHoverRightScroll(selectingTag = false) {
        const isExpanded = selectingTag ? this.isExpandedTags : this.isExpandedCategories;
        const target = selectingTag ? this.wrapperTags : this.wrapperFilters;

        if (!isExpanded) {
            this.scrollTimer = window.setInterval(() => {
                target.scroll({
                    left: target.scrollLeft + 40,
                    behavior: 'smooth',
                });
                this.handleLeftScrollerVisibility(selectingTag);
            }, 100);
        }
    }

    private openAdvancedFiltering() {
        this.dispatchEvent(new CustomEvent('open-advanced-filtering'));
    }

    private handleLeftScrollerVisibility(selectingTag = false) {
        const iconSpaceOne = 42;
        const leftPadding = 8;
        const target = selectingTag ? this.wrapperTags : this.wrapperFilters;
        const leftScroller = selectingTag ? this.leftScrollerTags : this.leftScrollerCategories;
        const cssPaddingVariable = selectingTag
            ? 'var(--user-tag-left-padding)'
            : 'var(--chip-left-padding)';

        if (isDefined(leftScroller) && target.scrollLeft > 0) {
            leftScroller.style.display = 'flex';
            this.style.setProperty(cssPaddingVariable, `${iconSpaceOne}px`);
        } else if (isDefined(leftScroller)) {
            leftScroller.style.display = 'none';
            this.style.setProperty(cssPaddingVariable, `${leftPadding}px`);
        }
    }

    private handleLeaveScroll(selectingTag = false) {
        clearInterval(this.scrollTimer);
    }

    private handleToggleExpander(selectingTag = false) {
        const target = selectingTag ? this.wrapperTags : this.wrapperFilters;
        const expander = selectingTag ? this.expanderTags : this.expanderCategories;

        if (selectingTag) {
            this.isExpandedTags = !this.isExpandedTags;
        } else {
            this.isExpandedCategories = !this.isExpandedCategories;
        }
        const isExpanded = selectingTag ? this.isExpandedTags : this.isExpandedCategories;

        clearInterval(this.scrollTimer);
        expander?.classList.toggle('expanded');

        if (isExpanded) {
            target.style.flexWrap = 'wrap';
        } else {
            target.style.flexWrap = 'nowrap';
        }
        this.handleLeftScrollerVisibility(selectingTag);
    }

    private clearAllClick() {
        this.isSelectedApprovalFilter = false;
        this.isSelectedCheckedInFilter = false;
        this.isSelectedPlaceFilter = undefined;
        this.isSpecialOfferSelected = false;
        this.isSelectedLikesFilter = false;
        this.dispatchEvent(new CustomEvent('clear-all-filters'));
    }

    private async handleCalendarClick() {
        await this.dateTimeRangeModal.open();
        logWithGoogleTag(FilterChips.amplitude_callsite, GoogleTagEvent.CHIP_CLICKED, {
            chipName: 'Date Time Filter',
            isTag: false,
            topic_id: topicData.topic?._id?.$oid,
        });
    }

    private toggleSpecialOfferSelection() {
        this.isSpecialOfferSelected = !this.isSpecialOfferSelected;

        logWithGoogleTag(FilterChips.amplitude_callsite, GoogleTagEvent.CHIP_CLICKED, {
            chipName: 'Special Offer Filter',
            isTag: false,
            topic_id: topicData.topic?._id?.$oid,
        });

        if (this.isSpecialOfferSelected) {
            pointCache.filterMarkers({
                name: 'SpecialOffer',
                filterFn: (p: GetPointResponse) => {
                    if (this.isSpecialOfferSelected) {
                        return Boolean(p.special_offer?.description);
                    }
                    return true;
                },
            });
        } else {
            pointCache.removeFilter('SpecialOffer');
        }
    }

    private toggleCheckedInSelection() {
        this.isSelectedCheckedInFilter = !this.isSelectedCheckedInFilter;

        logWithGoogleTag(FilterChips.amplitude_callsite, GoogleTagEvent.CHIP_CLICKED, {
            chipName: 'Checked In Filter',
            isTag: false,
            topic_id: topicData.topic?._id?.$oid,
        });

        this.handleChipClick({ target: { value: 'checkedin' } }, false, undefined, true, false, undefined, this.isSelectedApprovalFilter, this.isSelectedPlaceFilter, this.isSelectedLikesFilter);

    }

    private togglePlaceSelection(place: 1 | 2 | 3 | undefined | 'all') {
        this.isSelectedPlaceFilter = this.isSelectedPlaceFilter === place ? undefined : place;

        logWithGoogleTag(FilterChips.amplitude_callsite, GoogleTagEvent.CHIP_CLICKED, {
            chipName: 'Place Filter',
            isTag: false,
            topic_id: topicData.topic?._id?.$oid,
        });
        this.handleChipClick({ target: { value: 'place' } }, false, undefined, this.isSelectedCheckedInFilter, false, undefined, this.isSelectedApprovalFilter, this.isSelectedPlaceFilter, this.isSelectedLikesFilter);
    }

    private toggleLikesSelection() {
        this.isSelectedLikesFilter = !this.isSelectedLikesFilter;

        logWithGoogleTag(FilterChips.amplitude_callsite, GoogleTagEvent.CHIP_CLICKED, {
            chipName: 'Likes Filter',
            isTag: false,
            topic_id: topicData.topic?._id?.$oid,
        });

        this.handleChipClick({ target: { value: 'likes' } }, false, undefined, this.isSelectedCheckedInFilter, false, undefined, this.isSelectedApprovalFilter, this.isSelectedPlaceFilter, this.isSelectedLikesFilter);
    }

    private toggleTagsShowOption() {
        this.areTagsOpen = !this.areTagsOpen;
    }

    private toggleSubtagsShowOption(t: UserTag) {
        // the subtag being clicked is already selected, so we reset it
        if (this.openSubtag === t) {
            this.openSubtag = undefined;
        } else {
            this.openSubtag = t;
        }
    }

    private readonly triggerFilterEvent = async (e: any) => {
        await this.dateTimeRangeModal.close();

        if (isDefined(e.detail.startString) || isDefined(e.detail.endString)) {
            pointCache.filterMarkers({
                name: 'DateSearch',
                filterFn: (point) =>
                    filterPointByRange(point, e.detail.startString, e.detail.endString),
            });
        } else {
            pointCache.removeFilter('DateSearch');
        }
    };


    private async selectPointFromSearch(e: CustomEvent) {
        if (topicData.mapComponent) {
            const point = pointCache.points.find((p) => p._id.$oid === e.detail);
            if (!point) {
                console.warn('Point not found in cache:', e.detail.pointId);
                return;
            }
            await topicData.mapComponent.onClickPoint(point);
        }
        this.searchOpen = false;
    }

    private stopSearchScrollPropagation(e: WheelEvent) {
        e.stopPropagation();
    }

    /** Allow div-based filter buttons to be activated via Enter or Space */
    private handleFilterKeydown(e: KeyboardEvent, handler: () => void) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler();
        }
    }

    render() {
        const hidden = this.show ? '' : 'hidden';
        const advancedFiltersText = topicData.topic?.text_advanced_filters ?? 'Filters';
        const translatedAdvancedFiltersText = translationData.allTranslations.find((t) => t.original_text === advancedFiltersText)?.translated_text;
        return html`
            <div id="wrapperFilters" class="wrapper ${hidden}">
                ${this.pointListOpen && !detectMobileViewport() ? html`
                    <div
                        class="filter-button__likes filter-button__grey"
                        role="button"
                        tabindex="0"
                        aria-label="Close list view"
                        style="width:2rem; border-radius:50%; padding:0"
                        @click="${() => {topicData.mapComponent?.closeListOfPoints(); this.requestUpdate();}}"
                        @keydown="${(e: KeyboardEvent) => this.handleFilterKeydown(e, () => {topicData.mapComponent?.closeListOfPoints(); this.requestUpdate();})}"
                    >
                        <mwc-icon style="--mdc-icon-size: 20px;"
                            >
                            chevron_left
                        </mwc-icon>
                    </div>
                ` : ''}
                ${pointCache.hasFilterApplied ? html`
                    <paper-chip
                        label="${detectMobileViewport() ? '' : 'Clear All'}"
                        class="custom-chip clear-filters"
                        selectable
                        @click="${this.clearAllClick}"
                    >
                        <mwc-icon class="chip-background" slot="avatar"
                    >filter_alt_off</mwc-icon>
                    </paper-chip>`
                : ''}
                ${this.advancedFilterOn || this.simpleFilterOn ? html`
                    <paper-chip
                        label="${translatedAdvancedFiltersText ?? advancedFiltersText}"
                        class="custom-chip clear-filters ${this.advancedFilterOn ? 'advanced-on' : 'Filter'}"
                        selectable
                        @click="${() => this.openAdvancedFiltering()}"
                    >
                        <mwc-icon class="chip-background" slot="avatar" @click="${() => this.openAdvancedFiltering()}"
                        >filter_alt</mwc-icon>
                    </paper-chip>`
                : ''}
                ${topicData.topLevelUserTags.length > 0 && !this.advancedFilterOn && !this.simpleFilterOn
                    ? html`
                        ${topicData.allUserTags.length > 8 && topicData.topic?.show_advanced_filter_row !== true ? html`
                            <paper-chip
                                label="${translatedAdvancedFiltersText ?? advancedFiltersText}"
                                class="custom-chip clear-filters ${this.advancedFilterOn ? 'advanced-on' : 'Filter'}"
                                selectable
                                @click="${() => this.openAdvancedFiltering()}"
                            >
                                <mwc-icon class="chip-background" slot="avatar" @click="${() => this.openAdvancedFiltering()}">filter_alt</mwc-icon>
                            </paper-chip>
                        ` : html`
                            <div
                            id="tagsShowOption"
                            class="custom-chip"
                            role="button"
                            tabindex="0"
                            aria-label="${this.areTagsOpen ? 'Hide tag filters' : 'Show tag filters'}"
                            aria-expanded="${this.areTagsOpen}"
                            style=${this.selectedTopTags.length > 0
                                ? 'background: var(--main-primary-color); color:  var(--theme-white-color);'
                                : ''}
                            @click="${this.toggleTagsShowOption}"
                            @keydown="${(e: KeyboardEvent) => this.handleFilterKeydown(e, () => this.toggleTagsShowOption())}"
                            >
                            <mwc-icon>${topicData.topic?.hide_tag_filters === true ? 'filter_alt' : 'sell'}</mwc-icon>
                            ${this.areTagsOpen
                                ? html`
                                        <div class="tagInfo">
                                            <mwc-icon>arrow_drop_up</mwc-icon>
                                        </div>
                                    `
                                : html`
                                        <div class="tagInfo">
                                            ${this.selectedTopTags.length > 0
                                                ? this.selectedTopTags.length + this.selectedSubTags.length
                                                : html`<mwc-icon>arrow_drop_down</mwc-icon>`}
                                        </div>
                                    `}
                            </div>
                        `}
                    `
                : ''}
                ${pointCache.pointsAwaitingApproval.length > 0 && topicData.isWriter ? html`
                    <div
                        class="filter-button__approval ${!this.isSelectedApprovalFilter ? '' : 'filter-button__black'}"
                        @click="${(e: any) =>
                            this.handleChipClick(
                                e,
                                false,
                                undefined,
                                this.isSelectedCheckedInFilter,
                                false,
                                undefined,
                                !this.isSelectedApprovalFilter,
                                this.isSelectedPlaceFilter,
                                this.isSelectedLikesFilter
                            )}"
                    >
                            <mwc-icon style="--mdc-icon-size: 20px; ${this.isSelectedApprovalFilter ? 'color: white;' : ''}"
                                >
                                visibility_off
                            </mwc-icon>
                        </div>

                ` : ''}
                ${topicData.topic?.has_likes === true && !this.advancedFilterOn && !this.simpleFilterOn && getCookie('user_id') !== '' && isDefined(getCookie('user_id')) ? html`
                    <div
                        class="filter-button__likes ${this.isSelectedLikesFilter ? 'filter-button__red' : ''}"
                        role="button"
                        tabindex="0"
                        aria-label="${this.isSelectedLikesFilter ? 'Remove liked filter' : 'Show liked places'}"
                        aria-pressed="${this.isSelectedLikesFilter}"
                        style="width: ${this.isSelectedLikesFilter ? 'auto' : '2rem'}; border-radius: ${this.isSelectedLikesFilter ? '16px' : '50%'}; padding: ${this.isSelectedLikesFilter ? '0 8px' : '0'}"
                        @click="${this.toggleLikesSelection}"
                        @keydown="${(e: KeyboardEvent) => this.handleFilterKeydown(e, () => this.toggleLikesSelection())}"
                    >
                            <mwc-icon style="--mdc-icon-size: 20px;"
                                >
                                ${this.isSelectedLikesFilter ? 'check' : 'favorite'}
                            </mwc-icon>
                        ${this.isSelectedLikesFilter ? html`
                            You Liked
                        ` : ''}
                    </div>
                ` : ''}
                ${this.showCheckInFilter
                    ? html` <div
                        class="filter-button__checkin ${this.isSelectedCheckedInFilter
                            ? (this.hasActivatedChallenge ? 'filter-button__red' : '')
                            : ''}"
                        role="button"
                        tabindex="0"
                        aria-label="${this.isSelectedCheckedInFilter ? 'Remove checked-in filter' : 'Show checked-in places'}"
                        aria-pressed="${this.isSelectedCheckedInFilter}"
                        @click="${this.toggleCheckedInSelection}"
                        @keydown="${(e: KeyboardEvent) => this.handleFilterKeydown(e, () => this.toggleCheckedInSelection())}"
                    >
                        <img src="/images/checkbox_black.svg" width="20" height="20" alt="" />
                    </div>`
                    : ''}
                ${this.showElectionPlaceFilter
                    ? html`
                        <div class="filter-button__election_holder ${this.isSelectedPlaceFilter === 'all' ? 'filter-button__red' : ''}"
                            >
                            <span @click="${() => this.togglePlaceSelection('all')}">
                                ${!electionVotingIsHappening(topicData.activeElection) ? 'Winners' : 'Current Leaders'}
                            </span>
                            <div
                                class="filter-button__election ${this.isSelectedPlaceFilter === 1 ? 'filter-button__red' : ''}"
                                @click="${() => this.togglePlaceSelection(1)}"
                            >
                                <img src="/images/ic_first_place.png" width="20" height="20" />
                            </div>
                            ${topicData.activeElection?.show_leaders !== 1 ? html`
                                <div
                                    class="filter-button__election ${this.isSelectedPlaceFilter === 2 ? 'filter-button__red' : ''}"
                                    @click="${() => this.togglePlaceSelection(2)}"
                                >
                                    <img src="/images/ic_second_place.png" width="20" height="20" />
                                </div>
                                ${topicData.activeElection?.show_leaders === 3 ? html`
                                    <div
                                        class="filter-button__election ${this.isSelectedPlaceFilter === 3 ? 'filter-button__red' : ''}"
                                        @click="${() => this.togglePlaceSelection(3)}"
                                    >
                                        <img src="/images/ic_third_place.png" width="20" height="20" />
                                    </div>
                                ` : ''}
                            ` : ''}
                        </div>
                    `
                    : ''}
                ${pointCache.isReady && !this.advancedFilterOn && topicData.topic?.search_pill_settings?.enabled === true ? html`
                    ${!this.searchOpen ? html`
                        <div
                            class="filter-button__likes"
                            style="width: ${isDefined(undefinedIfEmpty(topicData.topic?.search_pill_settings?.pill_text)) ? 'auto' : '2rem'}; border-radius: ${isDefined(undefinedIfEmpty(topicData.topic?.search_pill_settings?.pill_text)) ? '16px' : '50%'}; padding: ${isDefined(undefinedIfEmpty(topicData.topic?.search_pill_settings?.pill_text)) ? '0 8px' : '0'}"
                            @click="${() => {
                                this.searchOpen = !this.searchOpen;
                            }}"
                        >
                                <mwc-icon style="--mdc-icon-size: 20px;">search
                                </mwc-icon>
                            ${isDefined(undefinedIfEmpty(topicData.topic?.search_pill_settings?.pill_text))? html`
                               ${topicData.topic?.search_pill_settings?.pill_text}
                            ` : ''}
                        </div>
                    ` : html`

                        <div
                            class="search-wrapper ${this.searchOpen ? 'search-open' : ''}"
                            @wheel="${this.stopSearchScrollPropagation}"
                        >
                                <proxi-autocomplete
                                    style="width: 100%;display: flex;flex-direction:column; min-width: 200px;"
                                    @on-select-point="${this.selectPointFromSearch}"
                                    @on-select-google="${this.selectPointFromSearch}"
                                    @on-select-category="${(e: CustomEvent) => {
                                        this.handleChipClick(
                                            e,
                                            false,
                                            e.detail,
                                            this.isSelectedCheckedInFilter,
                                            false,
                                            undefined,
                                            this.isSelectedApprovalFilter,
                                            this.isSelectedPlaceFilter,
                                            this.isSelectedLikesFilter
                                        );
                                    }}"
                                    @on-select-tag="${(e: CustomEvent) => {
                                        this.handleChipClick(
                                            e,
                                            true,
                                            e.detail,
                                            this.isSelectedCheckedInFilter,
                                            false,
                                            undefined,
                                            this.isSelectedApprovalFilter,
                                            this.isSelectedPlaceFilter,
                                            this.isSelectedLikesFilter
                                        );
                                    }}"
                                    @on-keyword-search="${this.keywordSearch}"
                                    @close-search="${() => {
                                        this.searchOpen = false;
                                    }}"
                                    @add-location-to-map="${async (e: CustomEvent) => {
                                        if (topicData.mapComponent) {
                                            await topicData.mapComponent.userPointCreateRequestFromLocation(e);
                                        }
                                    }}"
                                    .searchTypes="${topicData.topic?.search_pill_settings?.search_types}"
                                    isInFilterChip="${true}">
                                </proxi-autocomplete>
                        </div>
                    `}
                ` : ''}
                ${topicData.topic?.has_dates === true
                    ? html`
                        <div
                            class="filter-button__likes ${pointCache.filterFunctions.some((f) => f.name === 'DateSearch') ? 'filter-button__red' : ''}"
                            style="width: ${isDefined(undefinedIfEmpty(topicData.topic?.add_point_customization?.date_time?.header)) ? 'auto' : '2rem'}; border-radius: ${isDefined(undefinedIfEmpty(topicData.topic?.add_point_customization?.date_time?.header)) ? '16px' : '50%'}; padding: ${isDefined(undefinedIfEmpty(topicData.topic?.add_point_customization?.date_time?.header)) ? '0 8px' : '0'}"
                            @click="${this.handleCalendarClick}"
                        >
                                <mwc-icon style="--mdc-icon-size: 20px;">calendar_month
                                </mwc-icon>
                            ${isDefined(undefinedIfEmpty(topicData.topic?.add_point_customization?.date_time?.header))? html`
                               ${topicData.topic?.add_point_customization?.date_time?.header}
                            ` : ''}
                        </div>
                    `
                    : ``}
                ${pointCache.points.some((p) => p.special_offer?.description)
                    ? html`
                            <paper-chip
                            label="${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Offers'}"
                            class="custom-chip ${this.isSpecialOfferSelected && pointCache.hasFilterApplied
                                ? 'active'
                                : 'inactive'}"
                            selectable
                            @click="${this.toggleSpecialOfferSelection}"
                        >
                            <mwc-icon class="chip-background special-offer" slot="avatar" @click="${this.toggleSpecialOfferSelection}"
                                >
                                ${this.isSpecialOfferSelected ? 'check' : 'card_giftcard'}
                            </mwc-icon>
                        </paper-chip>
                    `
                    : ''}
                ${!this.advancedFilterOn && !this.simpleFilterOn ? html`
                    <div
                            id="leftScrollerCategories"
                            class="leftScroller scroller absolute"
                            @mouseenter="${() => this.handleHoverLeftScroll(false)}"
                            @mouseleave="${() => this.handleLeaveScroll(false)}"
                        >
                            <mwc-icon>chevron_left</mwc-icon>
                        </div>
                        ${topicData.categories.length > 1 && topicData.topic?.hide_category_filters !== true
                            ? topicData.categories.map((c) => {
                                const isSelected = this.selectedCategories?.includes(c.name);
                                const checkedClass = isSelected && pointCache.hasFilterApplied ? '' : 'displayNone';
                                const iconClass = (isSelected || !c.icon) && pointCache.hasFilterApplied ? 'displayNone' : '';
                                const translation = translationData.allTranslations.find((t) => t.original_text === c.name)?.translated_text;
                                return html`
                                    <paper-chip
                                        label="${translation ?? c.name}"
                                        value="${c.name}"
                                        class="custom-chip"
                                        @click="${(e: any) =>
                                            this.handleChipClick(
                                                e,
                                                false,
                                                c.name,
                                                this.isSelectedCheckedInFilter,
                                                false,
                                                undefined,
                                                this.isSelectedApprovalFilter,
                                                this.isSelectedPlaceFilter,
                                                this.isSelectedLikesFilter
                                            )}"
                                        selectable
                                    >
                                        <span class="chip-background" slot="avatar">
                                            <iron-icon
                                                icon="icons:check"
                                                class="${checkedClass}"
                                            ></iron-icon>
                                            <iron-icon src="${this.getCategoryChipIcon(c)}" class="${iconClass}"></iron-icon>
                                        </span>
                                    </paper-chip>
                                `;
                            })
                            : ''}
                        <div
                            id="expanderCategories"
                            class="expander scroller absolute"
                            @mouseenter="${() => this.handleHoverRightScroll(false)}"
                            @mouseleave="${() => this.handleLeaveScroll(false)}"
                            @click="${() => this.handleToggleExpander(false)}"
                        >
                            <mwc-icon id="seeAllCategories">chevron_right</mwc-icon>
                            <paper-tooltip for="seeAllCategories" offset="12"
                                >Click to see all categories</paper-tooltip
                            >
                        </div>
                    ` : ''}
            </div>
            <div id="wrapperTags" class="wrapper ${hidden}">
                ${topicData.topLevelUserTags.length > 0 && !this.advancedFilterOn && this.areTagsOpen && !this.simpleFilterOn
                    ?
                    html`
                        ${topicData.topic?.show_advanced_filter_row === true ? html `
                            <paper-chip
                                label="${translatedAdvancedFiltersText ?? advancedFiltersText}"
                                class="custom-chip clear-filters ${this.advancedFilterOn ? 'advanced-on' : 'Filter'}"
                                selectable
                                @click="${() => this.openAdvancedFiltering()}"
                            >
                                <mwc-icon class="chip-background" slot="avatar" @click="${() => this.openAdvancedFiltering()}">filter_alt</mwc-icon>
                            </paper-chip>
                        ` : ''}
                        <div
                              id="leftScrollerTags"
                              class="leftScroller scroller absolute"
                              @mouseenter="${() => this.handleHoverLeftScroll(true)}"
                              @mouseleave="${() => this.handleLeaveScroll(true)}"
                          >
                              <mwc-icon>chevron_left</mwc-icon>
                          </div>
                          ${topicData.topic?.hide_tag_filters !== true ? html`
                            ${topicData.topLevelUserTags.map((t: UserTag) => {
                                const isSelected = this.selectedTopTags?.some((tag) => tag.topLevelTag === t.name);
                                const iconName = isSelected && pointCache.hasFilterApplied ? 'check' : 'sell';
                                const tagTranslation = translationData.allTranslations.find((p) => p.original_text === t.name)?.translated_text;
                                // if there are subtags, render expander
                                if (t.tags && t.tags.length > 0) {
                                    return html`
                                        <div
                                            id="tagsShowOption"
                                            @click="${() => this.toggleSubtagsShowOption(t)}"
                                            style="background-color: ${t.icon}; color: ${getVisibleTextColor(
                                                t.icon
                                            )}"
                                        >
                                            <mwc-icon>sell</mwc-icon>
                                            <mwc-icon class="user-tag-group">sell</mwc-icon>
                                            ${tagTranslation ?? t.name}
                                            ${this.openSubtag === t
                                                ? html` <div class="tagInfo">
                                                        <mwc-icon>close</mwc-icon>
                                                    </div>`
                                                : html` <div class="tagInfo">
                                                        <mwc-icon>expand_more</mwc-icon>
                                                    </div>`}

                                        </div>
                                        ${this.openSubtag === t
                                            ? html`
                                                    ${t.tags.map((subtag) => {
                                                        const isSelected = this.selectedSubTags?.some(
                                                            (tag) => tag.topLevelTag === t.name && tag.subTags.includes(subtag.name)
                                                        );
                                                        const iconName = isSelected && pointCache.hasFilterApplied ? 'check' : 'sell';
                                                        const subtagTranslation = translationData.allTranslations.find((p) => p.original_text === subtag.name)?.translated_text;

                                                        return html`
                                                            <paper-chip
                                                                style="--paper-chip-background-color: ${subtag.icon}; --paper-chip-secondary-text-color: ${getVisibleTextColor(
                                                                    subtag.icon
                                                                )}"
                                                                label="${subtagTranslation ?? subtag.name}"
                                                                class="user-tag"
                                                                @click="${(e: any) =>
                                                                    this.handleChipClick(
                                                                        e,
                                                                        true,
                                                                        subtag.name,
                                                                        this.isSelectedCheckedInFilter,
                                                                        true,
                                                                        t.name,
                                                                        this.isSelectedApprovalFilter,
                                                                        this.isSelectedPlaceFilter,
                                                                        this.isSelectedLikesFilter
                                                                    )}"
                                                                selectable
                                                            >
                                                                <span
                                                                    class="chip-background"
                                                                    slot="avatar"
                                                                >
                                                                    ${subtag.name === 'Intentionalist' ? html`
                                                                        <img src="/images/intentionalist.jpg" width="20" height="20" />
                                                                    ` : html`
                                                                        <mwc-icon>${iconName}</mwc-icon>
                                                                    `}
                                                                </span>
                                                            </paper-chip>
                                                        `;
                                                    })}
                                                `
                                            : ''}
                                    `;
                                } else {
                                    return html`
                                        <paper-chip
                                            label="${t.name}"
                                            style="--paper-chip-background-color: ${t.icon}; --paper-chip-secondary-text-color: ${getVisibleTextColor(
                                                t.icon
                                            )}"
                                            class="user-tag"
                                            @click="${(e: any) =>
                                                this.handleChipClick(
                                                    e,
                                                    true,
                                                    t.name,
                                                    this.isSelectedCheckedInFilter,
                                                    false,
                                                    undefined,
                                                    this.isSelectedApprovalFilter,
                                                    this.isSelectedPlaceFilter,
                                                    this.isSelectedLikesFilter
                                                )}"
                                            selectable
                                        >
                                            <span class="chip-background" slot="avatar">
                                                ${t.name === 'Intentionalist' ? html`
                                                    <img src="/images/intentionalist.jpg" width="20" height="20" />
                                                ` : html`
                                                    <mwc-icon>${iconName}</mwc-icon>
                                                `}
                                            </span>
                                        </paper-chip>
                                    `;
                                }
                            })}
                        ` : ''}
                      `
                    : ''}
                <div
                    id="expanderTags"
                    ?hidden=${!this.areTagsOpen}
                    class="expander scroller absolute"
                    @mouseenter="${() => this.handleHoverRightScroll(true)}"
                    @mouseleave="${() => this.handleLeaveScroll(true)}"
                    @click="${() => this.handleToggleExpander(true)}"
                >
                    <mwc-icon id="seeAllCategories">chevron_right</mwc-icon>
                    <paper-tooltip for="seeAllCategories" offset="12"
                        >Click to see all tags</paper-tooltip
                    >
                </div>
            </div>
            <setting-drawer id="dateTimeRangeModal" title="Filter by Date/Time" @on-close="${async () => await this.dateTimeRangeModal.close()}">
                <date-time-range-modal @on-filter=${this.triggerFilterEvent} @on-clear-datetime-filter=${() => pointCache.removeFilter('DateSearch')}>
                </date-time-range-modal>
            </setting-drawer>
        `;
    }

    static get styles() {
        return css`
            :host {
                position: absolute;
                user-select: none;
                width: 100%;
                pointer-events: none;
                box-sizing: border-box;
                -webkit-overflow-scrolling: touch;
                top: 8px;
                z-index: 0;
            }

            #dateTimeRangeModal {
                --bottom-height: unset;
                --bottom-height-half: unset;
            }

            .wrapper {
                overflow: hidden;
                display: flex;
                min-height: 40px;
                align-items: flex-start;
            }

            .hidden {
                display: none;
            }

            .wrapper#wrapperFilters {
                padding: 0px 42px 0px var(--chip-left-padding, 8px);
            }

            .wrapper#wrapperTags {
                padding: 0px 42px 0px var(--user-tag-left-padding, 8px);
            }

            :host::-webkit-scrollbar {
                display: none;
            }

            paper-chip.custom-chip {
                --paper-chip-background-color: var(--theme-white-color);
                cursor: pointer;
                pointer-events: all;
                display: flex;
                flex-wrap: nowrap;
                align-items: center;
            }

            paper-chip.custom-chip.advanced-on {
                --paper-chip-background-color: var(--main-primary-color);
                --paper-chip-secondary-text-color: var(--theme-white-color);
                cursor: pointer;
            }

            paper-chip.user-tag {
                --paper-chip-background-color: var(--main-primary-color);
                --paper-chip-secondary-text-color: var(--theme-white-color);
                cursor: pointer;
                pointer-events: all;
            }

            paper-chip.clear-filters {
                --paper-chip-background-color: var(--theme-border-color);
                --paper-chip-secondary-text-color: var(--main-primary-color);
                cursor: pointer;
            }

            .user-tag-group {
                margin-left: -11px;
                margin-right: 4px;
            }

            .search-wrapper {
                style="overflow: hidden;
                border-radius: 16px;
                padding: 0;
                display:flex;
                align-items: center;
                justify-content: flex-start;
                flex-direction: row;
                pointer-events: auto;
                margin-right: 8px;
                max-width: 300px;
            }

            .calendar {
                border: 1px solid var(--theme-border-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
            }

            .calendar:hover {
                box-shadow: var(--shadow-elevation-2dp_-_box-shadow);
            }

            #tagsShowOption mwc-icon,
            paper-chip mwc-icon {
                font-size: 16px;
            }

            .displayNone {
                display: none;
            }

            .displayBlock {
                display: block;
            }

            .scroller {
                width: 2rem;
                height: 2rem;
                min-width: 2rem;
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                background: var(--theme-white-color);
                transition: all 0.2s linear;
                pointer-events: auto;
            }

            .expander.scroller::before {
                content: "";
                position: absolute;
                inset: -1px;
                border: 1px solid var(--main-primary-color);
                border-radius: 50%;
            }

            .scroller.absolute {
                position: absolute;
            }

            #specialOfferOption {
                margin-right: 5px;
                color: var(--main-special-offer-color);
                pointer-events: auto;
            }

            #specialOfferOption.active {
                background: var(--main-special-offer-color);
                color: var(--theme-white-color);
            }

            .expander {
                right: 8px;
                flex-wrap: nowrap;
                pointer-events: auto;
            }

            .leftScroller {
                left: 8px;
                display: none;
                z-index: 0;
                pointer-events: auto;
            }

            #openFilter {
                margin-right: 5px;
                pointer-events: auto;
            }

            #tagsShowOption {
                margin-right: 5px;
                background: var(--theme-white-color);
                color: var(--main-primary-color);
                height: 2rem;
                border-radius: 999px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0 0.5rem;
                line-height: 0.8rem;
                pointer-events: auto;
                border: 1px solid var(--theme-border-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
            }

            .tagInfo {
                min-width: 1.25rem;
                text-align: center;
            }

            .scroller:hover {
                box-shadow: var(--shadow-elevation-2dp_-_box-shadow);
            }

            .expander.expanded {
                background: var(--main-primary-color);
                flex-wrap: wrap;
            }

            .scroller mwc-icon {
                transition: all 0.2s linear;
            }

            .expander.expanded mwc-icon {
                transform: rotate(90deg);
                color: var(--theme-white-color);
            }

            .expander paper-tooltip {
                margin-left: -18px;
                text-align: center;
                display: block;
            }

            .expander.expanded paper-tooltip {
                display: none;
            }

            [role="button"]:focus-visible {
                outline: 2px solid var(--main-primary-color, #005fcc);
                outline-offset: 2px;
            }

            .filter-button__checkin {
                display: flex;
                flex-shrink: 0;
                align-items: center;
                justify-content: center;
                width: 2rem;
                height: 2rem;
                border-radius: 50%;
                cursor: pointer;
                pointer-events: all;
                margin: 0 5px;
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
            }

            .filter-button__likes {
                display: flex;
                flex-shrink: 0;
                align-items: center;
                justify-content: center;
                height: 2rem;
                cursor: pointer;
                pointer-events: all;
                margin: 0 5px;
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
            }

            .filter-button__election_holder {
                display: flex;
                flex-shrink: 0;
                align-items: center;
                justify-content: center;
                height: 2rem;
                border-radius: 16px;
                cursor: pointer;
                pointer-events: all;
                margin: 0 5px;
                padding-left: 10px;
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
            }

            .filter-button__election {
                display: flex;
                flex-shrink: 0;
                align-items: center;
                justify-content: center;
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 50%;
                cursor: pointer;
                pointer-events: all;
                margin: 0 5px;
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
            }

            .filter-button__approval {
                display: flex;
                flex-shrink: 0;
                align-items: center;
                justify-content: center;
                width: 2rem;
                height: 2rem;
                border-radius: 50%;
                cursor: pointer;
                pointer-events: all;
                margin: 0 5px;
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
            }

            .filter-button__blue {
                background-color: var(--main-surf-color);
            }

            .filter-button__black {
                background-color: black;
            }

            .filter-button__grey {
                background-color: var(--theme-border-color);
            }

            .filter-button__red {
                background-color: var(--main-invalid-color);
            }

            .special-offer {
                color: var(--main-special-offer-color);
            }

            .done {
                color: var(--light-theme-base-color);
                --mdc-icon-size: 16px;
            }

            @media only screen and (max-width: 550px) {
                :host {
                    z-index: 1; // so point cards appear above filter chips
                }

                #wrapper {
                    overflow-x: auto;
                    overflow-y: hidden;
                }

                .wrapper#wrapperFilters {
                    overflow-x: auto; // DO NOT REMOVE - this allows scrolling on mobile
                }

                .wrapper#wrapperTags {
                    overflow-x: auto; // DO NOT REMOVE - this allows scrolling on mobile
                }
            }
        `;
    }

    customizeStyles() {
        const chipButtons: any = this?.shadowRoot?.querySelectorAll('paper-chip');
        for (const elem of chipButtons) {
            const chipButton = elem?.shadowRoot?.querySelector('.chip');
            chipButton?.classList.remove('unselectable');

            const chipButtonStyle = elem?.shadowRoot?.querySelector('style');
            if (!isDefined(chipButtonStyle) || !isDefined(chipButton.innerHTML)) {
                return;
            }
            chipButtonStyle.innerHTML = `
                .chip {
                    display: flex;
                    flex-direction: row-reverse;
                    height: 32px;
                    font-size: var(--paper-chip-font-size, 13px);
                    font-weight: 500;
                    background-color: var(--paper-chip-background-color);
                    color: var(--paper-chip-secondary-text-color);
                    line-height: 32px;
                    padding: 0 4px 0 12px;
                    border-radius: 16px;
                    margin-bottom: 5px;
                    margin-right: 5px;
                    border: 1px solid var(--theme-border-color);
                    box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
                }

                .chip:active {
                    background: var(--theme-border-color);
                }

                .chip .closeIcon {
                    margin-left: 4px;
                    cursor: pointer;
                    float: right;
                    width: 12px;
                }

                .chip .inline {
                    display: -webkit-inline-box;
                }

                .hoverEffect:hover {
                    box-shadow: var(--shadow-elevation-2dp_-_box-shadow);
                    cursor: pointer;
                }

                .unselectable {
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    -khtml-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                }

                .close {
                    top: -1px;
                }

                iron-icon {
                    --iron-icon-height: 16px;
                    --iron-icon-width: 16px;
                    position: relative;
                    right: 8px;
                    border-radius: 50%;
                }

                .label {
                    white-space: nowrap;
                    margin-right: 8px;
                }

                .avatar ::slotted(.chip-image) {
                    float: left;
                    margin: 0 8px 0 -12px;
                    height: 32px;
                    width: 32px;
                    border-radius: 50%;
                }

                .avatar ::slotted(.chip-background) {
                    --iron-icon-height: 22px;
                    --iron-icon-width: 22px;
                    border-radius: 50%;
                    float: left;
                    font-weight: bold;
                    font-size: 1rem;
                    width: 2rem;
                    height: 2rem;
                    margin:  0 0px 0 -10px;
                    text-align: center;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                [hidden] {
                    display: none;
                }
            `;
        }
        this.resizeSlider(8, true);
        this.resizeSlider(50, false);
    }
}
